import {
  type DeliveryOrderDetail,
  DeliveryPreferenceType,
  type Ledger,
  type Order,
  OrderType,
} from '@sg/graphql-schema';

import { getMaxTipAmount, type OrderingCart } from '@order/features/ordering';

import { type PartialLineItem } from '../../../graphql';
import { mapWantedTimes } from './wantedTimes';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A function that takes an {Order} and returns the cart info to be sent to the machine.
 */
export const prepareCart = (cart: Partial<Order>): OrderingCart => {
  const {
    id: orderId = '',
    orderType,
    restaurant,
    deliveryOrderDetail: maybeDeliveryOrderDetail,
    availableWantedTimes: availableTimes,
    lineItems: maybeLineItems,
    canTrackOrderStatus = false,
    ledger = {} as Ledger,
  } = cart;

  const {
    isOutpost,
    id: restaurantId = '',
    slug: restaurantSlug = '',
    name: restaurantName = '',
    deliveryMinSubtotal = 0,
    availableDropOffLocations,
    showDeliveryFeeDisclosure = false,
    showOutpostPriceDifferenciationDisclosure = false,
    showDeliveryPriceDifferenciationDisclosure = false,
  } = restaurant ?? {};

  const deliveryOrderDetail = (maybeDeliveryOrderDetail ??
    {}) as DeliveryOrderDetail;

  const { address, deliveryFee = 0 } = deliveryOrderDetail;

  const { id: addressId, name: addressName } = address ?? {};

  const locationName =
    orderType === OrderType.Delivery
      ? (addressName ?? restaurantName)
      : restaurantName;

  const deliveryPreferences = {
    type: address?.deliveryPreference ?? DeliveryPreferenceType.LeaveAtDoor,
    notes: address?.notes ?? '',
  };

  const orderChannel: OrderingCart['orderChannel'] = (
    isOutpost ? 'outpost' : (orderType?.toLowerCase() ?? 'pickup')
  ) as OrderingCart['orderChannel'];

  const wantedTimes = mapWantedTimes(availableTimes);

  const dropoffLocations =
    availableDropOffLocations?.map((entry) => ({
      value: entry.id,
      label: entry.name.toLowerCase(),
    })) ?? [];

  const lineItems = (maybeLineItems ?? []) as readonly PartialLineItem[];

  const appliedRewardId = ledger?.discounts?.[0]?.id ?? undefined;
  const maxTipAmount = getMaxTipAmount(ledger);

  return {
    orderId,
    orderChannel,
    locationName,
    wantedTimes,
    dropoffLocations,
    restaurantId,
    restaurantSlug,
    addressId,
    deliveryOrderDetail,
    deliveryPreferences,
    deliveryMinSubtotal,
    canTrackOrderStatus,
    lineItems,
    ledger,
    appliedRewardId,
    showDeliveryFeeDisclosure,
    showOutpostPriceDifferenciationDisclosure,
    showDeliveryPriceDifferenciationDisclosure,
    deliveryFee,
    maxTipAmount,
  };
};
